<template>
	<div class="nn-avatar-editor" :class="{ 'is-editing': newAvatar !== null }" v-cloak>
		<div class="nn-avatar-editor__viewport">
			<img
				v-show="newAvatar === null"
				ref="image"
				class="nn-avatar-editor__image"
				:src="userAvatar"
				:alt="`Profielfoto van ${profileName}`"
			>
			<img
				v-show="newAvatar !== null"
				ref="imageCropper"
				class="nn-avatar-editor__image"
				:src="newAvatar"
				:alt="`Nieuwe profielfoto van ${profileName}`"
			>

			<div v-if="loading" class="nn-avatar-editor__loading" role="status" aria-live="polite">
				<loading-indicator></loading-indicator>
				<span class="nn-sr-only">Profielfoto opslaan</span>
			</div>
		</div>

		<div v-if="newAvatar === null" class="nn-avatar-editor__actions">
			<button class="uk-button nn-avatar-editor__button" type="button" @click="selectImage">
				<span uk-icon="icon: camera" aria-hidden="true"></span>
				Foto wijzigen
			</button>
		</div>

		<div v-else class="nn-avatar-editor__actions nn-avatar-editor__actions--editing">
			<button
				class="uk-button uk-button-default nn-avatar-editor__button"
				type="button"
				:disabled="loading"
				@click="cancel"
			>Annuleren</button>
			<button
				class="uk-button uk-button-primary nn-avatar-editor__button"
				type="button"
				:disabled="loading || !cropper"
				@click="save"
			>Opslaan</button>
		</div>

		<p v-if="errorMessage" class="nn-avatar-editor__error" role="alert" v-text="errorMessage"></p>

		<form ref="avatarForm" enctype="multipart/form-data" accept-charset="UTF-8" hidden>
			<input type="hidden" :name="csrfName" :value="csrfToken">
			<input type="hidden" name="action" value="users/save-user">
			<input type="hidden" name="userId" :value="userId">
			<input
				ref="fileInput"
				type="file"
				name="photo"
				accept="image/png,image/jpeg,image/webp"
				@change="handleFileChange"
			>
		</form>
	</div>
</template>

<script>
import Cropper from 'cropperjs'
import 'cropperjs/dist/cropper.css'
import axios from 'axios'
import LoadingIndicator from './LoadingIndicator.vue'

export default {
	name: 'AvatarCropper',
	components: { LoadingIndicator },
	props: {
		userId: {
			type: Number,
			required: true,
			validator: (value) => value > 0,
		},
		currentAvatar: {
			type: String,
			required: true,
		},
		profileName: {
			type: String,
			default: 'NieuwNieuws-gebruiker',
		},
	},
	data() {
		return {
			loading: false,
			errorMessage: '',
			userAvatar: this.currentAvatar,
			newAvatar: null,
			cropper: null,
			cropperOptions: {
				aspectRatio: 1,
				autoCropArea: 0.9,
				viewMode: 1,
			},
			outputOptions: {
				width: 512,
				height: 512,
				imageSmoothingEnabled: true,
				imageSmoothingQuality: 'high',
			},
		};
	},
	computed: {
		csrfName() {
			return window.csrfTokenName;
		},
		csrfToken() {
			return window.csrfTokenValue;
		},
	},
	beforeUnmount() {
		this.destroyCropper();
	},
	methods: {
		selectImage(event) {
			event.preventDefault();
			this.errorMessage = '';
			this.$refs.fileInput.value = '';
			this.$refs.fileInput.click();
		},
		handleFileChange(event) {
			const file = event.target.files?.[0];

			if (!file) {
				return;
			}

			this.destroyCropper();
			this.errorMessage = '';

			const reader = new FileReader();
			reader.onerror = () => {
				this.errorMessage = 'Deze afbeelding kon niet worden geopend.';
			};
			reader.onload = async (readerEvent) => {
				this.newAvatar = readerEvent.target?.result || null;

				if (!this.newAvatar) {
					this.errorMessage = 'Deze afbeelding kon niet worden geopend.';
					return;
				}

				await this.$nextTick();
				this.cropper = new Cropper(this.$refs.imageCropper, this.cropperOptions);
			};
			reader.readAsDataURL(file);
		},
		destroyCropper() {
			if (this.cropper) {
				this.cropper.destroy();
				this.cropper = null;
			}
		},
		cancel() {
			this.destroyCropper();
			this.newAvatar = null;
			this.loading = false;
			this.errorMessage = '';
			this.$refs.fileInput.value = '';
		},
		save() {
			if (!this.cropper || this.loading) {
				return;
			}

			const canvas = this.cropper.getCroppedCanvas(this.outputOptions);

			if (!canvas) {
				this.errorMessage = 'De uitsnede kon niet worden gemaakt.';
				return;
			}

			const preview = canvas.toDataURL('image/jpeg', 0.9);
			this.loading = true;
			this.errorMessage = '';

			canvas.toBlob((blob) => {
				if (!blob) {
					this.loading = false;
					this.errorMessage = 'De profielfoto kon niet worden verwerkt.';
					return;
				}

				const formData = new FormData(this.$refs.avatarForm);
				formData.append('photo', blob, `avatar_${Date.now()}.jpg`);

				axios({
					method: 'post',
					url: '/',
					responseType: 'json',
					data: formData,
					headers: {
						'Accept': 'application/json'
					},
				})
				.then((response) => {
					if (!response.data?.id) {
						throw new Error(response.data?.message || 'De profielfoto kon niet worden opgeslagen.');
					}

					this.userAvatar = preview;
					this.loading = false;
					this.cancel();
				})
				.catch((requestError) => {
					this.loading = false;
					this.errorMessage = requestError.response?.data?.message || requestError.message || 'De profielfoto kon niet worden opgeslagen.';
				});
			}, 'image/jpeg', 0.9);
		},
	},
};
</script>
